import React, { useState, useEffect } from 'react'
import { 
  Button, 
  Typography, 
  Form, 
  Input, 
  Switch, 
  message, 
  Modal,
  Card,
  Avatar,
  Spin
} from 'antd'
import { 
  ArrowLeftOutlined, 
  UserOutlined, 
  UploadOutlined,
  ShopOutlined,
  PhoneOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { User } from '../types'
import { wechatLogin } from '../utils/wechat'
import { userAPI, dealerAPI } from '../services/api'
import { mergeLoginUserWithServerProfile, normalizeSyncWecom } from '../utils/userMerge'
import Cropper from 'react-cropper'
import 'cropperjs/dist/cropper.css'
import './UserEditPage.css'

const { Title, Text } = Typography

interface Dealer {
  id: number
  name: string
  address: string
}

const UserEditPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, updateUser } = useAuth()
  const [form] = Form.useForm()
  const [phoneLoading, setPhoneLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [userInfo, setUserInfo] = useState<User | null>(user)
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null)
  const [pageLoading, setPageLoading] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)
  
  // 头像相关状态
  const [avatarModalVisible, setAvatarModalVisible] = useState(false)
  const [cropper, setCropper] = useState<any>(null)
  const [avatarPreview, setAvatarPreview] = useState<string>('')
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  useEffect(() => {
    console.log('🔍 UserEditPage useEffect triggered:', { user: !!user, hasInitialized })
    if (user && !hasInitialized) {
      // 只在初始加载时从后端获取最新的用户信息
      console.log('📡 开始获取用户信息...')
      fetchCurrentUserInfo()
      setHasInitialized(true)
    }
  }, [user, hasInitialized])

  // 从后端获取当前用户的最新信息
  const fetchCurrentUserInfo = async () => {
    console.log('🔄 fetchCurrentUserInfo 开始执行')
    setPageLoading(true)
    try {
      const response = await userAPI.getCurrentUser() as any
      if (response.success && response.data) {
        const currentUser = response.data
        const base = user ?? (currentUser as User)
        const merged = mergeLoginUserWithServerProfile(base as User, currentUser as User)
        setUserInfo(merged)
        updateUser(merged as User)
        form.setFieldsValue({
          consultant_phone: merged.consultant_phone,
          is_sync_wecom: normalizeSyncWecom(merged.is_sync_wecom),
        })
        
        // 如果用户有经销商ID，获取经销商详细信息
        if (currentUser.dealer_id) {
          await fetchDealerInfo(currentUser.dealer_id)
        }
      } else {
        // 如果获取失败，使用缓存的用户信息作为备选
        setUserInfo(user)
        form.setFieldsValue({
          consultant_phone: user?.consultant_phone,
          is_sync_wecom: normalizeSyncWecom(user?.is_sync_wecom),
        })
        
        if (user?.dealer_id) {
          await fetchDealerInfo(user.dealer_id)
        }
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      // 如果获取失败，使用缓存的用户信息作为备选
      setUserInfo(user)
      form.setFieldsValue({
        consultant_phone: user?.consultant_phone,
        is_sync_wecom: normalizeSyncWecom(user?.is_sync_wecom),
      })
      
      if (user?.dealer_id) {
        await fetchDealerInfo(user.dealer_id)
      }
    } finally {
      console.log('✅ fetchCurrentUserInfo 执行完成，设置 pageLoading 为 false')
      setPageLoading(false)
    }
  }

  // 获取经销商信息（dealerId 可为 UUID 字符串或数字）
  const fetchDealerInfo = async (dealerId: number | string) => {
    try {
      const response = await dealerAPI.getDealerDetail(dealerId) as any
      if (response.success && response.data) {
        setSelectedDealer({
          id: response.data.id,
          name: response.data.name,
          address: response.data.address
        })
      }
    } catch (error) {
      console.error('获取经销商信息失败:', error)
    }
  }

  // 处理从经销商选择页面返回
  useEffect(() => {
    const state = location.state as any
    console.log('🔍 经销商选择 useEffect 触发:', { state })
    if (state?.selectedDealer) {
      setSelectedDealer(state.selectedDealer)
      
      // 如果经销商已更新，同时更新本地用户信息
      if (state.dealerUpdated) {
        // 使用函数式更新来获取最新的userInfo
        setUserInfo(prevUserInfo => {
          if (prevUserInfo) {
            const updatedUser = { ...prevUserInfo, dealer_id: state.selectedDealer.id }
            updateUser(updatedUser)
            return updatedUser
          }
          return prevUserInfo
        })
      }
      
      // 清除location.state，避免重复处理
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [location.state, updateUser]) // 移除userInfo依赖，避免循环触发

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleAvatarClick = () => {
    // 先显示模态框，让裁剪器初始化
    setAvatarModalVisible(true)
  }

  // 在模态框内处理文件选择
  const handleFileSelect = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        console.log('选择的文件:', file)
        const reader = new FileReader()
        reader.onload = (e) => {
          const result = e.target?.result as string
          console.log('文件读取完成，设置预览:', result ? '有内容' : '无内容')
          setAvatarPreview(result)
        }
        reader.readAsDataURL(file)
      }
    }
    input.click()
  }


  const handleCropAvatar = () => {
    if (cropper) {
      const canvas = cropper.getCroppedCanvas({
        width: 200,
        height: 200,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      })
      
      if (!canvas) {
        message.error('裁剪失败，请重试')
        return
      }
      
      canvas.toBlob(async (blob: Blob | null) => {
        if (!blob) {
          message.error('裁剪失败，请重试')
          return
        }
        
        setUploadingAvatar(true)
        try {
          console.log('开始上传头像，blob大小:', blob.size)
          
          const formData = new FormData()
          formData.append('avatar', blob, 'avatar.jpg')
          
          console.log('FormData准备完成，开始上传...')
          const response = await userAPI.uploadAvatar(formData) as any
          
          console.log('上传响应:', response)
          
          if (response.success) {
            const newAvatarUrl = response.data.avatar_url
            console.log('头像上传成功，新URL:', newAvatarUrl)
            
            // 更新本地用户信息
            setUserInfo(prevUserInfo => {
              if (prevUserInfo) {
                const updatedUser = { ...prevUserInfo, avatar_url: newAvatarUrl }
                updateUser(updatedUser)
                return updatedUser
              }
              return prevUserInfo
            })
            
            // 如果当前启用了企业微信同步，则自动关闭
            if (userInfo?.is_sync_wecom) {
              await handleAutoDisableSyncWecom('头像')
            }
            
            message.success('头像更新成功')
            setAvatarModalVisible(false)
            setAvatarPreview('')
          } else {
            console.error('上传失败:', response.message)
            message.error(response.message || '头像上传失败')
          }
        } catch (error) {
          console.error('头像上传失败:', error)
          message.error('头像上传失败')
        } finally {
          setUploadingAvatar(false)
        }
      }, 'image/jpeg', 0.9)
    } else {
      message.error('裁剪器未初始化，请重试')
    }
  }

  const handleDealerSelect = () => {
    navigate('/dealer-selection', {
      state: { 
        returnTo: '/user-edit',
        selectedDealer: selectedDealer
      }
    })
  }

  // 单独更新电话号码
  const handleUpdatePhone = async () => {
    const phoneValue = form.getFieldValue('consultant_phone')
    if (!phoneValue) {
      message.error('请输入电话号码')
      return
    }
    
    // 验证手机号格式
    const phoneRegex = /^1[3-9]\d{9}$/
    if (!phoneRegex.test(phoneValue)) {
      message.error('请输入正确的手机号码')
      return
    }

    setPhoneLoading(true)
    try {
      const updateData = {
        consultant_phone: phoneValue
      }

      const response = await userAPI.updateProfile(updateData) as any
      if (response.success) {
        const serverUser = response.user as User | undefined
        if (serverUser) {
          setUserInfo(serverUser)
          updateUser(serverUser)
        } else {
          setUserInfo(prevUserInfo => {
            if (prevUserInfo) {
              const updatedUser = { ...prevUserInfo, consultant_phone: phoneValue }
              if ((updatedUser.dealer_id == null || updatedUser.dealer_id === '') && selectedDealer?.id != null) {
                updatedUser.dealer_id = selectedDealer.id
              }
              updateUser(updatedUser)
              return updatedUser
            }
            return prevUserInfo
          })
        }
        
        // 如果当前启用了企业微信同步，则自动关闭（不会清空所属经销商）
        if (userInfo?.is_sync_wecom) {
          await handleAutoDisableSyncWecom('电话号码')
        }
        
        message.success('电话号码更新成功')
      } else {
        message.error(response.message || '更新失败')
      }
    } catch (error) {
      console.error('更新电话号码失败:', error)
      message.error('更新失败，请重试')
    } finally {
      setPhoneLoading(false)
    }
  }

  // 单独更新企业微信同步设置
  const handleUpdateSyncWecom = async (checked: boolean) => {
    setSyncLoading(true)
    try {
      const updateData = {
        is_sync_wecom: checked
      }

      const response = await userAPI.updateProfile(updateData) as any
      if (response.success) {
        const serverUser = response.user as User | undefined
        if (serverUser) {
          setUserInfo(serverUser)
          updateUser(serverUser)
          form.setFieldsValue({
            is_sync_wecom: normalizeSyncWecom(serverUser.is_sync_wecom),
            consultant_phone: serverUser.consultant_phone,
          })
        } else {
          setUserInfo(prevUserInfo => {
            if (prevUserInfo) {
              const updatedUser = { ...prevUserInfo, is_sync_wecom: checked }
              if ((updatedUser.dealer_id == null || updatedUser.dealer_id === '') && selectedDealer?.id != null) {
                updatedUser.dealer_id = selectedDealer.id
              }
              updateUser(updatedUser)
              return updatedUser
            }
            return prevUserInfo
          })
        }
        message.success(`企业微信同步已${checked ? '开启' : '关闭'}`)
      } else {
        message.error(response.message || '更新失败')
        // 如果更新失败，恢复开关状态
        form.setFieldsValue({ is_sync_wecom: !checked })
      }
    } catch (error) {
      console.error('更新企业微信同步设置失败:', error)
      message.error('更新失败，请重试')
      // 如果更新失败，恢复开关状态
      form.setFieldsValue({ is_sync_wecom: !checked })
    } finally {
      setSyncLoading(false)
    }
  }

  // 自动关闭企业微信同步（联动功能）；不清空用户已选择保存的所属经销商
  const handleAutoDisableSyncWecom = async (actionType: string) => {
    try {
      const updateData = {
        is_sync_wecom: false
      }

      const response = await userAPI.updateProfile(updateData) as any
      if (response.success) {
        const serverUser = response.user as User | undefined
        if (serverUser) {
          setUserInfo(serverUser)
          updateUser(serverUser)
          form.setFieldsValue({
            is_sync_wecom: normalizeSyncWecom(serverUser.is_sync_wecom),
            consultant_phone: serverUser.consultant_phone,
          })
        } else {
          setUserInfo(prevUserInfo => {
            if (prevUserInfo) {
              const updatedUser = { ...prevUserInfo, is_sync_wecom: false }
              if ((updatedUser.dealer_id == null || updatedUser.dealer_id === '') && selectedDealer?.id != null) {
                updatedUser.dealer_id = selectedDealer.id
              }
              updateUser(updatedUser)
              return updatedUser
            }
            return prevUserInfo
          })
          form.setFieldsValue({ is_sync_wecom: false })
        }
        
        // 显示联动提示
        message.info(`由于您手动更新了${actionType}，企业微信同步已自动关闭`)
      } else {
        console.error('自动关闭企业微信同步失败:', response.message)
      }
    } catch (error) {
      console.error('自动关闭企业微信同步失败:', error)
    }
  }


  return (
    <div className="user-edit-page">
      {/* 顶部导航 */}
      <div className="page-header">
        <div className="header-content">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-btn"
          >
            返回
          </Button>
          <Title level={4} className="page-title">
            个人信息
          </Title>
          <div className="header-spacer"></div>
        </div>
      </div>

      {/* 页面内容 */}
      <div className="page-content">
        <Spin spinning={pageLoading} tip="读取中...">
          <Form
            form={form}
            layout="vertical"
            className="user-edit-form"
          >
          {/* 头像区域 */}
          <Card className="avatar-card">
            <div className="avatar-section">
              <div className="avatar-container" onClick={handleAvatarClick}>
                <Avatar
                  size={80}
                  src={wechatLogin.getAvatarUrl(userInfo?.avatar_url)}
                  icon={<UserOutlined />}
                  className="user-avatar"
                />
                <div className="avatar-overlay">
                  <UploadOutlined />
                  <Text className="avatar-text">点击更换头像</Text>
                </div>
              </div>
              <div className="avatar-info">
                <Title level={5} className="avatar-title">
                  {userInfo?.consultant_name || '用户'}
                </Title>
                <Text type="secondary" className="avatar-desc">
                  点击头像可以上传和裁剪新头像
                </Text>
              </div>
            </div>
          </Card>

          {/* 电话号码 */}
          <Card className="info-card">
            <Title level={5} className="card-title">电话号码</Title>
            
            <div className="phone-section">
              <Form.Item
                name="consultant_phone"
                rules={[
                  { required: true, message: '请输入电话号码' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }
                ]}
                className="phone-form-item"
              >
                <Input 
                  type="tel"
                  inputMode="numeric"
                  prefix={<PhoneOutlined />}
                  placeholder="请输入电话号码"
                  size="large"
                  maxLength={11}
                />
              </Form.Item>
              <Button
                type="primary"
                loading={phoneLoading}
                onClick={handleUpdatePhone}
                className="update-phone-btn"
              >
                更新电话
              </Button>
            </div>
          </Card>

          {/* 经销商信息 */}
          <Card className="dealer-card">
            <Title level={5} className="card-title">所属经销商</Title>
            <div className="dealer-section" onClick={handleDealerSelect}>
              <div className="dealer-info">
                <ShopOutlined className="dealer-icon" />
                <div className="dealer-details">
                  {selectedDealer ? (
                    <>
                      <Text className="dealer-name">{selectedDealer.name}</Text>
                      <Text type="secondary" className="dealer-address">
                        {selectedDealer.address}
                      </Text>
                    </>
                  ) : (
                    <Text type="secondary">请选择经销商</Text>
                  )}
                </div>
              </div>
              <Button 
                type="default" 
                size="large" 
                className="dealer-select-btn"
                block
              >
                选择经销商
              </Button>
            </div>
          </Card>

          {/* 企业微信同步设置 */}
          <Card className="sync-card">
            <Title level={5} className="card-title">企业微信同步</Title>
            <div className="sync-section">
              <div className="sync-header">
                <div className="sync-info">
                  <SyncOutlined className="sync-icon" />
                  <Text className="sync-title">自动同步企业微信信息</Text>
                </div>
                <Form.Item name="is_sync_wecom" valuePropName="checked" className="sync-switch">
                  <Switch 
                    loading={syncLoading}
                    onChange={handleUpdateSyncWecom}
                  />
                </Form.Item>
              </div>
              <Text type="secondary" className="sync-desc">
                开启后，登录时会自动从企业微信同步姓名、电话和头像
              </Text>
            </div>
          </Card>

          </Form>
        </Spin>
      </div>

      {/* 头像裁剪模态框 */}
      <Modal
        title="裁剪头像"
        open={avatarModalVisible}
        onCancel={() => {
          setAvatarModalVisible(false)
          setAvatarPreview('')
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setAvatarModalVisible(false)
            setAvatarPreview('')
          }}>
            取消
          </Button>,
          <Button
            key="crop"
            type="primary"
            loading={uploadingAvatar}
            onClick={handleCropAvatar}
            disabled={!avatarPreview}
          >
            确定
          </Button>
        ]}
        width={400}
        centered
      >
        <div className="cropper-container">
          {avatarPreview ? (
            <Cropper
              style={{ height: 300, width: '100%' }}
              aspectRatio={1}
              src={avatarPreview}
              viewMode={1}
              guides={true}
              minCropBoxHeight={100}
              minCropBoxWidth={100}
              background={false}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false}
              onInitialized={(instance: any) => {
                setCropper(instance)
              }}
            />
          ) : (
            <div className="file-select-area">
              <div className="file-select-content">
                <UploadOutlined className="file-select-icon" />
                <Text className="file-select-text">请选择要裁剪的图片</Text>
                <Button 
                  type="primary" 
                  onClick={handleFileSelect}
                  className="file-select-btn"
                >
                  选择图片
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default UserEditPage
